export interface MapBounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface MapTransform {
  tx: number;
  ty: number;
  scale: number;
}

export interface Viewport {
  width: number;
  height: number;
}

// The scale at which the bounds fit the viewport, leaving `padding` (a fraction
// of the viewport) on each side
export const fitScaleForBounds = (
  bounds: MapBounds,
  viewport: Viewport,
  padding = 0
) => {
  const width = viewport.width * (1 - 2 * padding);
  const height = viewport.height * (1 - 2 * padding);
  return Math.min(width / bounds.width, height / bounds.height);
};

// How far past the fitted view a zone can be zoomed into
const ZONE_ZOOM_RANGE = 4;

// The maximum zoom with bounds: never below the fitted scale, so a small zone
// that needs more than the default maximum to fit can still be zoomed in and out
export const maxZoomForBounds = (maxZoom: number, fitScale: number) =>
  Math.max(maxZoom, fitScale * ZONE_ZOOM_RANGE);

// How far past the fitted scale a zone has to be zoomed before the map tilts
const ZONE_TILT_ABOVE_FIT = 1.25;

// The zoom from which the map tilts and to which focusing zooms. With bounds the
// fitted overview of a small zone can be above `desiredZoom`; tilting starts
// above the fit instead, so the overview stays flat
export const tiltZoomForBounds = (desiredZoom: number, fitScale: number) =>
  Math.max(desiredZoom, fitScale * ZONE_TILT_ABOVE_FIT);

/**
 * Keeps a pan/zoom transform inside the bounds: it can't zoom out further than
 * the bounds fitting the viewport, and while the bounds are larger than the
 * viewport on an axis, no space outside them shows on that axis. When they are
 * smaller, they're centred. Bounds are in the map element's own pixels (before
 * the transform), the same space as `tx`/`ty`.
 */
export const constrainToBounds = (
  transform: MapTransform,
  bounds: MapBounds,
  viewport: Viewport,
  padding = 0
): MapTransform => {
  const scale = Math.max(
    transform.scale,
    fitScaleForBounds(bounds, viewport, padding)
  );

  const axis = (
    translate: number,
    start: number,
    size: number,
    available: number
  ) => {
    const scaled = size * scale;
    if (scaled <= available) return (available - scaled) / 2 - start * scale;
    const max = -start * scale;
    const min = available - (start + size) * scale;
    return Math.min(max, Math.max(min, translate));
  };

  return {
    tx: axis(transform.tx, bounds.x, bounds.width, viewport.width),
    ty: axis(transform.ty, bounds.y, bounds.height, viewport.height),
    scale
  };
};

export interface ViewBoxMapping {
  sx: number;
  sy: number;
  ox: number;
  oy: number;
}

/**
 * How a viewBox maps to an element of the given size under its
 * preserveAspectRatio (default "xMidYMid meet"): "none" stretches each axis on
 * its own, "meet"/"slice" scale both axes the same and align the leftover space.
 */
export const viewBoxMapping = (
  viewBox: MapBounds,
  size: Viewport,
  preserveAspectRatio = "xMidYMid meet"
): ViewBoxMapping => {
  const [align = "xMidYMid", mode = "meet"] = preserveAspectRatio
    .replace(/^defer\s+/, "")
    .trim()
    .split(/\s+/);
  const sx = size.width / viewBox.width;
  const sy = size.height / viewBox.height;
  if (align === "none") {
    return { sx, sy, ox: 0 - viewBox.x * sx, oy: 0 - viewBox.y * sy };
  }

  const scale = mode === "slice" ? Math.max(sx, sy) : Math.min(sx, sy);
  const offset = (available: number, used: number, position: string) =>
    position === "Min"
      ? 0
      : position === "Max"
        ? available - used
        : (available - used) / 2;
  return {
    sx: scale,
    sy: scale,
    ox:
      offset(size.width, viewBox.width * scale, align.slice(1, 4)) -
      viewBox.x * scale,
    oy:
      offset(size.height, viewBox.height * scale, align.slice(5, 8)) -
      viewBox.y * scale
  };
};

// The mapping from the SVG's user (map) units to the element's own pixels,
// through its viewBox when it has one. Layout sizes only: a bounding rect would
// include the pan/zoom transform applied to the map.
const svgMapping = (svg: SVGSVGElement): ViewBoxMapping | null => {
  const viewBox = svg.viewBox?.baseVal;
  const width = svg.clientWidth || svg.width?.baseVal?.value || 0;
  const height = svg.clientHeight || svg.height?.baseVal?.value || 0;
  if (!viewBox?.width || !viewBox.height || !width || !height) return null;
  return viewBoxMapping(
    viewBox,
    { width, height },
    svg.getAttribute?.("preserveAspectRatio") ?? undefined
  );
};

// Converts bounds in the SVG's user (map) units to the SVG element's own pixels
export const mapBoundsToSvgPixels = (
  svg: SVGSVGElement,
  bounds: MapBounds
): MapBounds => {
  const mapping = svgMapping(svg);
  if (!mapping) return bounds;
  return {
    x: bounds.x * mapping.sx + mapping.ox,
    y: bounds.y * mapping.sy + mapping.oy,
    width: bounds.width * mapping.sx,
    height: bounds.height * mapping.sy
  };
};

// The translate that centres a rect (in the map element's own pixels, like the
// result of mapBoundsToSvgPixels) in the viewport at the given scale, moved by
// the optional offsets (in the same pixels, before scaling)
// A focus target in the space transforms use: the map element's pixels when the
// map is limited to zone bounds, otherwise the SVG user units unchanged
export const focusTargetRect = (
  svg: SVGSVGElement,
  rect: MapBounds,
  hasBounds: boolean
): MapBounds => (hasBounds ? mapBoundsToSvgPixels(svg, rect) : rect);

export const translateToCenter = (
  rect: MapBounds,
  scale: number,
  viewport: Viewport,
  offset: { left?: number; top?: number } = {}
) => ({
  tx:
    viewport.width / 2 - (rect.x + (offset.left ?? 0) + rect.width / 2) * scale,
  ty:
    viewport.height / 2 - (rect.y + (offset.top ?? 0) + rect.height / 2) * scale
});

export interface MapPoint {
  x: number;
  y: number;
}

// Converts points in the SVG's user (map) units to the SVG element's own
// pixels, like mapBoundsToSvgPixels
export const mapPointsToSvgPixels = (
  svg: SVGSVGElement,
  points: MapPoint[]
): MapPoint[] => {
  const mapping = svgMapping(svg);
  if (!mapping) return points;
  return points.map((point) => ({
    x: point.x * mapping.sx + mapping.ox,
    y: point.y * mapping.sy + mapping.oy
  }));
};

// Twice the signed area: positive when the points go clockwise on screen
const signedArea = (points: MapPoint[]) =>
  points.reduce((sum, point, i) => {
    const next = points[(i + 1) % points.length];
    return sum + point.x * next.y - next.x * point.y;
  }, 0);

/**
 * A CSS clip-path that keeps only the polygons, one subpath each, so a zone
 * made of separate pieces shows just those. Every polygon is drawn in the same
 * direction so overlapping pieces add up rather than cut a hole. Undefined
 * without a polygon of at least three points.
 */
export const clipPathForPolygons = (polygons: MapPoint[][]) => {
  const subpaths = polygons
    .filter((polygon) => polygon.length >= 3)
    .map((polygon) =>
      signedArea(polygon) < 0 ? [...polygon].reverse() : polygon
    )
    .map(
      (polygon) =>
        `M ${polygon.map((point) => `${round(point.x)} ${round(point.y)}`).join(" L ")} Z`
    );
  return subpaths.length > 0 ? `path('${subpaths.join(" ")}')` : undefined;
};

const round = (value: number) => Math.round(value * 100) / 100;
